import { useEffect, useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import CreateBannerForm from "src/features/vendorSponsorshipAdmin/components/CreateBannerForm/CreateBannerForm";
import adaptEditFullpageTakeoverForm from "../../adapters/adaptEditFullpageTakeoverForm";
import adaptFullPageTakeoverForm from "../../adapters/adaptFullPageTakeoverForm";
import VENDOR_FULL_PAGE_TAKEOVER_FORM from "../../constants/vendorStepsForm";
import useActiveFullScreenTakeover from "../../hooks/useActiveFullScreenTakeover/useActiveFullScreenTakeover";
import useEditFullScreenTakeover from "../../hooks/useEditFullScreenTakeover/useEditFullScreenTakeover";

const EditFullScreenTakeover = () => {
	const navigate = useNavigate();
	const { formId } = useParams<{ formId: number }>();

	const [defaultValues, setDefaultValues] = useState<
		BannerFormState & { audienceGroupIds?: number[] }
	>({
		bannerStyle: "standard",
		bannerType: "fullScreenTakeoverBanner",
		selectedHashtag: null,
		feedId: null,
		steps: VENDOR_FULL_PAGE_TAKEOVER_FORM,
		audienceGroupIds: []
	});

	const { data: banner } = useActiveFullScreenTakeover(formId);

	const { mutate: editForm } = useEditFullScreenTakeover();

	const onSuccess = () => {
		toast.success(<CustomToast type="Success" message="Edited Successfully" />);
		navigate(
			"/vendor-sponsorship/full-screen-takeover/banners?published=false"
		);
	};

	const onError = () => {
		toast.error(
			<CustomToast type="Error" message="There is an error editing the form" />
		);
	};

	const onSubmit: SubmitHandler<BannerFormState> = (data) => {
		const audienceGroupIds = (
			data as BannerFormState & {
				audienceGroupIds?: number[];
			}
		).audienceGroupIds;

		editForm(
			{
				...adaptEditFullpageTakeoverForm(formId, data),
				audience_group_ids: audienceGroupIds ?? []
			} as CarouselBanner & { audience_group_ids: number[] },
			{
				onSuccess,
				onError
			}
		);
	};

	useEffect(() => {
		if (formId && banner) {
			const bannerWithAudience = banner as CarouselBanner & {
				audience_groups?: { id: number; name: string }[];
				open_in_browser?: boolean;
				feed?: { uuid: string; name: string } | null;
			};

			setDefaultValues((prev) => ({
				...prev,
				bannerType: banner.template,
				steps: adaptFullPageTakeoverForm(banner),
				buttonLink: banner.button_link ?? "",
				openInBrowser: bannerWithAudience.open_in_browser ?? false,
				linkFeedId: bannerWithAudience.feed?.uuid ?? null,
				selectedLinkFeed: bannerWithAudience.feed
					? {
							uuid: bannerWithAudience.feed.uuid,
							name: bannerWithAudience.feed.name
						}
					: null,
				audienceGroupIds: (bannerWithAudience.audience_groups ?? []).map(
					(g) => g.id
				)
			}));
		}
	}, [banner, formId]);

	return (
		<CreateBannerForm
			onSubmit={onSubmit}
			defaultValues={defaultValues}
			bannerTypeRadioOptions={["fullScreenTakeoverBanner"]}
			showBannerTypePicker={false}
			showAudienceGroups
			root="full-screen-takeover"
		/>
	);
};

export default EditFullScreenTakeover;
